import {
    VOTED_FIELD,
    REALIZED_FIELD,
    SECONDARY_BUDGET_AXIS_ELEMENT_NAME_FIELD,
    SECONDARY_BUDGET_AXIS_ELEMENT_CODE,
} from 'routes/BudgetModule/config/constants';
import { ColDef, ValueFormatterParams } from 'ag-grid-community';
import { translateBlockValue } from 'i18n';
import { getUrl } from 'routes/BudgetModule/components/BudgetProposal/Resource/ExpenseIncome/Operations/helpers/gridHelpers';
import {
    amountMetricToPhase,
    getFieldForPhase,
} from 'routes/BudgetModule/shared/phases/helpers';
import { TRACKABLE_EVENTS, trackEventBudget } from 'helpers/trackerHelpers';
import { ORDERED_LIST_METRIC } from './dataHelpers';
import { SecondaryBudgetAxisElementT } from 'routes/BudgetModule/config/types';
import { CURRENCY_EXCEL_CLASSNAMES } from 'routes/BudgetModule/config/excelExportHelpers';
import { currencyFormatter } from 'helpers/formatHelper';

const VOTED_FIELD_CAMEL = VOTED_FIELD;
const REALIZED_FIELD_CAMEL = REALIZED_FIELD;

/** Translate block value with exercice: used to find header label */
const translateFieldWithExercice = (field: string, exercice: number): string =>
    `${translateBlockValue(field, true)} ${exercice}`;

const getDisabled = (operation: SecondaryBudgetAxisElementT) =>
    !operation[SECONDARY_BUDGET_AXIS_ELEMENT_CODE];

/** This Function is used to retrieve the Columns inside the operation view */
export const getColumns = (
    exercice: number,
    primaryColor: string,
    t: TranslationT,
): ColDef[] => {
    const cellStylePrimaryBold = {
        color: primaryColor,
        fontWeight: 600,
        justifyContent: 'flex-end',
    };
    const cellStyleBold = { fontWeight: 600, justifyContent: 'flex-end' };
    const cellStyleSemiBold = { fontWeight: 510 };
    const numericCol: ColDef = {
        sortable: true,
        filter: 'agNumberColumnFilter',
        type: 'numericColumn',
        floatingFilterComponent: 'numberFloatingFilter',
        aggFunc: 'sum',
        cellStyle: cellStyleBold,
        valueFormatter: (params: ValueFormatterParams<unknown, number>) =>
            currencyFormatter(params.value ?? 0, { maximumFractionDigits: 0 }),
        useValueFormatterForExport: false,
        cellClass: CURRENCY_EXCEL_CLASSNAMES,
    };
    const columns: ColDef[] = [
        {
            field: SECONDARY_BUDGET_AXIS_ELEMENT_NAME_FIELD,
            headerName: translateBlockValue(
                SECONDARY_BUDGET_AXIS_ELEMENT_NAME_FIELD,
                true,
            ),
            sortable: true,
            sort: 'asc',
            filter: 'agTextColumnFilter',
            floatingFilterComponent: 'textFloatingFilter',
            enableRowGroup: true,
            cellStyle: cellStyleSemiBold,
            cellRenderer: 'agGroupCellRenderer',
            cellRendererParams: {
                getUrl,
                getDisabled,
                target: '_blank',
                innerRenderer: 'linkCellRenderer',
                style: {
                    fontWeight: '510',
                    textDecoration: 'underline',
                },
                onClick: (row: ResponseDataT) => {
                    trackEventBudget(
                        TRACKABLE_EVENTS.budget.budgetOverview.operations
                            .operationClicked,
                        {
                            exercice,
                            operation:
                                row[SECONDARY_BUDGET_AXIS_ELEMENT_NAME_FIELD],
                        },
                    );
                },
            },
        },
    ];

    // -------- Amounts depending on exercice amount -------- //

    // Primitif n-1
    columns.push({
        ...numericCol,
        field: VOTED_FIELD_CAMEL,
        headerName: translateFieldWithExercice(VOTED_FIELD_CAMEL, exercice - 1),
    });

    // Réalisé n-1
    columns.push({
        ...numericCol,
        field: REALIZED_FIELD_CAMEL,
        headerName: translateFieldWithExercice(
            REALIZED_FIELD_CAMEL,
            exercice - 1,
        ),
    });

    // -------- Amount depending on the operation budget's phase -------- //
    ORDERED_LIST_METRIC.forEach((metricPhase) => {
        const phase = amountMetricToPhase[metricPhase];
        const header = getFieldForPhase(phase, 'getPhaseHeaderName')(
            exercice,
            t,
        );
        columns.push({
            ...numericCol,
            field: metricPhase,
            headerName: header,
            cellStyle: cellStylePrimaryBold,
        });
    });

    return columns;
};
